import { SubscriptionFooter } from '@components/SubscriptionFooter';
import { useToast } from '@contexts/Toast';
import { Axios } from '@helpers/ApiService';
import { errorMessage } from '@helpers/Constants';
import { Assets } from '@helpers/Constants/Assets';
import { GiftBoxIcon, TickIcon } from '@helpers/Constants/Icons';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import { SubscriptionPlanProps } from '@helpers/Models';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export const PlansView = ({
  isNewUser,
  isFromAppSettings,
}: {
  isNewUser: boolean;
  isFromAppSettings?: boolean;
}) => {
  const navigate = useNavigate();

  const [subscriptions, setSubscriptions] = useState<SubscriptionPlanProps>({
    id: 0,
    planName: '',
    description: '',
    price: 0,
    billingCycle: '',
    trialDays: 0,
    trialMaxAnimals: 0,
    isActive: 0,
  });
  const { showToastMessage } = useToast();

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  /**
   * Fetches the current subscription details from the backend.
   *
   * @async
   * @function fetchSubscriptionDetails
   * @returns {Promise<void>}
   *
   * @remarks
   * - Retrieves subscription data using the API endpoint.
   * - Updates the local state with the fetched subscription details.
   * - Stores the latest subscription status in `localStorage` for global access.
   * - Displays an error toast message if the request fails.
   */
  const fetchSubscriptions = async (): Promise<void> => {
    try {
      const response = await Axios.get(ApiEndpoints.FETCH_SUBSCRIPTION);
      setSubscriptions(response.data.data.subscriptions[0]);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  return (
    <div className="p-3">
      <div className="flex min-h-screen flex-1 flex-col justify-center px-3 pb-5 pt-12 lg:px-3">
        {isNewUser && (
          <div className="sm:mx-auto sm:w-full sm:max-w-sm">
            <img alt="Logo Img" src={Assets.LOGO} className="mx-auto h-12" />
          </div>
        )}
        <div className="w-full">
          <h2 className="mt-3 text-center text-4xl font-bold tracking-tight text-gray-900">
            Choose Your Subscription Plan
          </h2>
          <span className="text-sm text-[#6B7280] mt-2 flex items-center justify-center whitespace-nowrap">
            Select the perfect plan for your butcher business and start managing
            orders efficiently
          </span>
        </div>
        {/* The following code may be required in future to show offer value. */}
        {/* Coupon banner */}
        {/* <div className="mt-6 py-3 bg-[#F8F8F8]">
                                <div className="flex items-center justify-center gap-2 text-center">
                                 <span className="justify-center">
                                     <svg width="20" height="16" viewBox="0 0 20 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                         <path d="M10 13C10.2833 13 10.5208 12.9042 10.7125 12.7125C10.9042 12.5208 11 12.2833 11 12C11 11.7167 10.9042 11.4792 10.7125 11.2875C10.5208 11.0958 10.2833 11 10 11C9.71667 11 9.47917 11.0958 9.2875 11.2875C9.09583 11.4792 9 11.7167 9 12C9 12.2833 9.09583 12.5208 9.2875 12.7125C9.47917 12.9042 9.71667 13 10 13ZM10 9C10.2833 9 10.5208 8.90417 10.7125 8.7125C10.9042 8.52083 11 8.28333 11 8C11 7.71667 10.9042 7.47917 10.7125 7.2875C10.5208 7.09583 10.2833 7 10 7C9.71667 7 9.47917 7.09583 9.2875 7.2875C9.09583 7.47917 9 7.71667 9 8C9 8.28333 9.09583 8.52083 9.2875 8.7125C9.47917 8.90417 9.71667 9 10 9ZM10 5C10.2833 5 10.5208 4.90417 10.7125 4.7125C10.9042 4.52083 11 4.28333 11 4C11 3.71667 10.9042 3.47917 10.7125 3.2875C10.5208 3.09583 10.2833 3 10 3C9.71667 3 9.47917 3.09583 9.2875 3.2875C9.09583 3.47917 9 3.71667 9 4C9 4.28333 9.09583 4.52083 9.2875 4.7125C9.47917 4.90417 9.71667 5 10 5ZM18 16H2C1.45 16 0.979167 15.8042 0.5875 15.4125C0.195833 15.0208 0 14.55 0 14V10C0.55 10 1.02083 9.80417 1.4125 9.4125C1.80417 9.02083 2 8.55 2 8C2 7.45 1.80417 6.97917 1.4125 6.5875C1.02083 6.19583 0.55 6 0 6V2C0 1.45 0.195833 0.979167 0.5875 0.5875C0.979167 0.195833 1.45 0 2 0H18C18.55 0 19.0208 0.195833 19.4125 0.5875C19.8042 0.979167 20 1.45 20 2V6C19.45 6 18.9792 6.19583 18.5875 6.5875C18.1958 6.97917 18 7.45 18 8C18 8.55 18.1958 9.02083 18.5875 9.4125C18.9792 9.80417 19.45 10 20 10V14C20 14.55 19.8042 15.0208 19.4125 15.4125C19.0208 15.8042 18.55 16 18 16ZM18 14V11.45C17.3833 11.0833 16.8958 10.5958 16.5375 9.9875C16.1792 9.37917 16 8.71667 16 8C16 7.28333 16.1792 6.62083 16.5375 6.0125C16.8958 5.40417 17.3833 4.91667 18 4.55V2H2V4.55C2.61667 4.91667 3.10417 5.40417 3.4625 6.0125C3.82083 6.62083 4 7.28333 4 8C4 8.71667 3.82083 9.37917 3.4625 9.9875C3.10417 10.5958 2.61667 11.0833 2 11.45V14H18Z" fill="#170B00" />
                                     </svg>
                                 </span>
                                 <span className="text-[#170B00] text-lg">
                                     First-time user? Get 20% OFF!</span>
                                  </div>
                             </div> */}
        <div className="sm:mx-auto sm:w-full sm:max-w-[28rem] mt-6">
          <div className="mt-5">
            {/* Subscription plan card */}
            <div
              className="p-3"
              style={{
                borderWidth: '4px 2px 2px 2px',
                borderStyle: 'solid',
                borderImage:
                  'linear-gradient(224.86deg, #73CCD8 4.87%, #2B6B9F 96.04%) 1',
              }}
            >
              {/* Subscription name */}
              <div className="text-[#292D32] flex flex-col items-center">
                <span className="font-extrabold text-xl">
                  {subscriptions.planName}
                </span>
                <span className="text-[#7F7F7F]">
                  {subscriptions.description}
                </span>
              </div>
              {/* Price */}
              <div className="text-[#292D32] flex flex-col items-center">
                <div className="mt-1">
                  <span className="font-bold text-[#292D32]">$</span>
                  <span className="text-4xl"> {subscriptions.price}</span>
                  <span className="mb-3"> / animal</span>
                </div>
                <span className="mt-3">{subscriptions.billingCycle}</span>
              </div>
              {/* Show the trial details only for new users */}
              {isNewUser && (
                <div className="mt-6 p-2 flex items-center justify-center bg-[#F8F8F8] text-[#170B00]">
                  <span>
                    <GiftBoxIcon width={20} height={20} fill="#434343" />
                  </span>
                  <span className="ml-2">
                    {subscriptions.trialDays}-day trial or{' '}
                    {subscriptions.trialMaxAnimals ?? 0} animals
                  </span>
                </div>
              )}

              <div className="p-2 mt-4">
                <button
                  type="submit"
                  onClick={() => {
                    if (isFromAppSettings) {
                      navigate(
                        FrontendRoutes.SUBSCRIPTION_REACTIVATE_PLANS_DETAILS,
                      );
                    } else {
                      navigate(FrontendRoutes.VIEW_PLAN_DETAILS, {
                        state: {
                          from: 'subscription',
                        },
                      });
                    }
                  }}
                  className={`w-full px-6 py-1 border border-transparent rounded-sm text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center space-x-2`}
                >
                  Start Subscription
                </button>
                {isNewUser && (
                  <button
                    type="button"
                    onClick={() =>
                      navigate(FrontendRoutes.VIEW_PLAN_DETAILS, {
                        state: {
                          from: 'trial',
                        },
                      })
                    }
                    className={`w-full mt-3 px-6 py-1 border rounded-sm text-sm font-medium bg-white text-primary border-primary hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary`}
                  >
                    Start Free Trial
                  </button>
                )}
              </div>
            </div>

            {/* Features */}
            <div className="flex-1 mb-6 mt-5 md:mt-8 lg:mt-8 xl:mt-5 shadow-lg">
              <table className="w-full table-auto">
                {/* Table Header */}
                <thead className="bg-[#F8F9FA]">
                  <tr className="border-b text-[#6C757D] text-sm uppercase">
                    <th className="font-medium px-2 py-3 w-1/2 text-left">
                      Features
                    </th>
                    <th className="font-medium py-1 w-1/2 text-left">
                      Price Per Animal Plan
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      Animals Per Month
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-center">
                      Unlimited
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      Price Per Animal
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-center">
                      $ {subscriptions.price}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      Billing Cycle
                    </td>
                    <td className="px-4 py-3 text-sm font-medium  text-center">
                      {subscriptions.billingCycle}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      Order Management
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <TickIcon width={17} height={12} fill="#10B981" />
                        <span className="text-[#10B981] text-sm font-medium">
                          Included
                        </span>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      Automated Notifications
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <TickIcon width={17} height={12} fill="#10B981" />
                        <span className="text-[#10B981] text-sm font-medium">
                          Included
                        </span>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                      Advanced analytics dashboard
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <TickIcon width={17} height={12} fill="#10B981" />
                        <span className="text-[#10B981] text-sm font-medium">
                          Included
                        </span>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      Customer database
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <TickIcon width={17} height={12} fill="#10B981" />
                        <span className="text-[#10B981] text-sm font-medium">
                          Included
                        </span>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      Email support
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <TickIcon width={17} height={12} fill="#10B981" />
                        <span className="text-[#10B981] text-sm font-medium">
                          Included
                        </span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        {/* Footer */}
        <SubscriptionFooter isFromAppSettings={true} />
      </div>
    </div>
  );
};
